import axios from 'axios';
import store from '@/store';
import appConfig from '@/utils/config';
import locale from '@/utils/init-locale';
import isTokenValid from '@/utils/token';
import tryRefreshToken from '@/api/res-error-try-refresh';
import responseHandleError from '@/api/res-handle-error';

const baseConfig = {
  baseURL: `${appConfig.API_BASE_URL}/`,
};

const axiosInstance = axios.create({
  ...baseConfig,
  withCredentials: true,
});

// Inctance without interceptors
axiosInstance.$token = axios.create(baseConfig);

const jwtacc = localStorage.getItem('jwtacc');

if (jwtacc) {
  axiosInstance.defaults.headers.jwtacc = jwtacc;
}
if (locale) {
  axiosInstance.defaults.headers['x-user-locale'] = locale;
}

const handleStorageChange = (event) => {
  if (event.key === 'jwtacc') {
    if (event.newValue === null) {
      delete axiosInstance.defaults.headers.jwtacc;
    } else {
      axiosInstance.defaults.headers.jwtacc = event.newValue;
    }
  }
};

window.onstorage = handleStorageChange;

axiosInstance.interceptors.request.use(
  async (config) => {
    if (axiosInstance.defaults.headers.jwtacc
       && !isTokenValid(axiosInstance.defaults.headers.jwtacc)) {
      axiosInstance.$app.$log.debug('Token expired in interceptor before call');

      const app = axiosInstance.$app;

      const error = new Error('Token Expired');
      error.config = config;
      error.response = { status: 401 };
      error.status = error.response.status;

      try {
        app.$log.debug('pre-try refresh token');
        // We need to await here in order to catch rejection
        const token = await tryRefreshToken(error, app);
        app.$log.debug('pre-token refreshed');

        config.headers.jwtacc = token;
        return config;
      } catch (err) {
        app.$log.debug('pre-error refresh token', err.message);
        // Do not perform "double route to sign-in"
        store.commit('signOut');

        throw error;
      }
    }

    return config;
  },
  (error) => {
    axiosInstance.$app.$log.debug('Toast Request interceptor Error: ', error.message);
    axiosInstance.$app.$toast(error.message, {
      title: axiosInstance.$app.$t('toast.danger'),
      variant: 'danger',
      solid: true,
      autoHideDelay: 15000,
    });
    return Promise.reject(error);
  },
);

axiosInstance.interceptors.response.use(
  (response) => (response),
  async (error) => (responseHandleError(error, axiosInstance.$app, store)),
);

axiosInstance.mockApiError = (error) => {
  const { $log, $sentry } = axiosInstance.$app;
  // API errors catched here. If custom handler is not requried

  $log.debug(`Mock error: ${error.message}\n${error.stack}`);
  // !error.response -> network error - hide error
  // error.response && error.response.statusCode === 401 -> auth error -> hide error
  if ($sentry) {
    $sentry.captureException(error, {
      tags: {
        section: '$http',
        mocked: true,
      },
    });
  }
};

export default axiosInstance;
